/**
 * Release item side-panel / bottom-sheet controller (spec-feature.md §3.4:
 * "サイドパネル(デスクトップ)/ボトムシート(モバイル)").
 *
 * A release page (internal/site/templates/releases.templ's
 * `releaseItemPanel`) renders an empty, hidden `#release-panel` container.
 * Each inline annotation's "詳細を見る" link
 * (`a.release-detail-link[data-item-id]`, built in
 * internal/site/templates/release_markdown.go) already points at the
 * Item's standalone detail page
 * (`/releases/go1.{MINOR}/{item_id}.html`), so plain navigation always
 * works (progressive enhancement, and the only behavior on the standalone
 * page itself, which has no panel).
 *
 * When JS is available and a panel exists, clicking such a link is
 * intercepted: the standalone detail page is fetched, its
 * `.release-item-detail` content is extracted and cloned into the panel,
 * the panel is shown, and `history.pushState` syncs the URL to the
 * release page's own path plus the item's fragment
 * (/releases/go1.NN/#item_id) — deliberately NOT the standalone page's
 * URL: the fragment keeps the reader on the release page (an anchor
 * there scrolls to the same item, matching the TOC/copy-link anchors),
 * while the standalone .html page remains the no-JS/OGP/search target
 * via the link's plain href (spec-feature.md §3.4's URL-sync intent,
 * amended by user preference for fragment URLs).
 */

const PANEL_ID = 'release-panel';
const PANEL_BODY_ID = 'release-panel-body';
const BACKDROP_ID = 'release-panel-backdrop';
const DETAIL_CONTENT_SELECTOR = '.release-item-detail';
const DETAIL_LINK_SELECTOR = 'a.release-detail-link[data-item-id]';
const PROPOSAL_LINK_SELECTOR = 'a.release-proposal-link[data-item-id]';

/**
 * What the panel is showing: an item's own detail page, or the weekly
 * proposal-digest page its annotation links to. Determines which content
 * container to extract from the fetched page, the header title, and which
 * link popstate should replay.
 */
export type PanelKind = 'detail' | 'proposal';

const PANEL_KIND_CONFIG: Record<PanelKind, { selector: string; linkSelector: string; title: string }> = {
  detail: { selector: DETAIL_CONTENT_SELECTOR, linkSelector: DETAIL_LINK_SELECTOR, title: '項目の詳細' },
  proposal: { selector: 'article.proposal-detail', linkSelector: PROPOSAL_LINK_SELECTOR, title: 'プロポーザル' },
};

interface ReleasePanelHistoryState {
  releasePanelItemId?: string;
  releasePanelKind?: PanelKind;
}

function getPanel(): HTMLElement | null {
  return document.getElementById(PANEL_ID);
}

function getPanelBody(): HTMLElement | null {
  return document.getElementById(PANEL_BODY_ID);
}

function getBackdrop(): HTMLElement | null {
  return document.getElementById(BACKDROP_ID);
}

function findPanelLink(itemId: string, kind: PanelKind): HTMLAnchorElement | null {
  return document.querySelector<HTMLAnchorElement>(
    `${PANEL_KIND_CONFIG[kind].linkSelector.replace('[data-item-id]', '')}[data-item-id="${itemId}"]`,
  );
}

/**
 * Fetches href (a release-item detail page), extracts its
 * `.release-item-detail` content, and renders it into the panel body.
 * Exported for testing.
 */
export async function loadPanelContent(href: string, contentSelector: string = DETAIL_CONTENT_SELECTOR): Promise<void> {
  const body = getPanelBody();
  if (!body) return;

  body.innerHTML = '<p class="p-6 text-sm text-[var(--text-muted)]">読み込み中…</p>';

  try {
    const res = await fetch(href);
    if (!res.ok) {
      throw new Error(`fetch failed with status ${res.status}`);
    }
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector(contentSelector);
    if (!content) {
      throw new Error('detail content not found in fetched page');
    }
    body.innerHTML = '';
    const imported = document.importNode(content, true) as HTMLElement;
    // Strip page chrome from the embedded page: breadcrumbs and
    // "...に戻る" links navigate the standalone page, which is meaningless
    // inside the panel (it has its own close affordance and the reader
    // never left the release page).
    imported.querySelectorAll('nav').forEach((nav) => nav.remove());
    body.appendChild(imported);
  } catch (err) {
    body.innerHTML = '<p class="p-6 text-sm text-red-600">詳細の読み込みに失敗しました。</p>';
    console.error('release-panel: failed to load item detail', err);
  }
}

/**
 * Opens the panel showing itemId's detail content, fetched from href.
 * Exported for testing.
 */
export async function openReleasePanel(href: string, itemId: string, pushHistory: boolean, kind: PanelKind = 'detail'): Promise<void> {
  const panel = getPanel();
  if (!panel) {
    // No panel on this page (e.g. the standalone detail page itself):
    // fall back to normal navigation.
    window.location.href = href;
    return;
  }

  panel.hidden = false;
  panel.setAttribute('data-open', 'true');
  getBackdrop()?.removeAttribute('hidden');
  document.body.classList.add('release-panel-open');

  const title = document.getElementById('release-panel-title');
  if (title) title.textContent = PANEL_KIND_CONFIG[kind].title;

  if (pushHistory) {
    // Both kinds sync the URL to the item's fragment: whatever the panel
    // shows, it is "about" that item, and the fragment doubles as the
    // item's on-page anchor. The state's kind disambiguates for popstate.
    const state: ReleasePanelHistoryState = { releasePanelItemId: itemId, releasePanelKind: kind };
    const releaseHref = panel.dataset.releaseHref || window.location.pathname;
    history.pushState(state, '', `${releaseHref}#${itemId}`);
  }

  await loadPanelContent(href, PANEL_KIND_CONFIG[kind].selector);
}

/**
 * Closes the panel. If pushHistory is true and releaseHref is given, the URL
 * is restored to the release page itself. Exported for testing.
 */
export function closeReleasePanel(pushHistory: boolean, releaseHref?: string): void {
  const panel = getPanel();
  if (!panel) return;

  panel.hidden = true;
  panel.removeAttribute('data-open');
  getBackdrop()?.setAttribute('hidden', '');
  document.body.classList.remove('release-panel-open');

  if (pushHistory && releaseHref) {
    history.pushState({}, '', releaseHref);
  }
}

let globalListenersAttached = false;

/**
 * Wires up click interception on every "詳細を見る" link on the page, plus
 * the panel's close controls and popstate handling. Safe to call multiple
 * times (per-element listeners are idempotent; document/window-level
 * listeners are attached at most once).
 */
export function initializeReleasePanel(): void {
  const panel = getPanel();
  if (!panel) return; // Not a release page (or panel markup absent).

  const releaseHref = panel.dataset.releaseHref || window.location.pathname;

  (Object.keys(PANEL_KIND_CONFIG) as PanelKind[]).forEach((kind) => {
    document.querySelectorAll<HTMLAnchorElement>(PANEL_KIND_CONFIG[kind].linkSelector).forEach((link) => {
      link.addEventListener('click', (event) => {
        const mouseEvent = event as MouseEvent;
        if (
          mouseEvent.defaultPrevented ||
          mouseEvent.button !== 0 ||
          mouseEvent.metaKey ||
          mouseEvent.ctrlKey ||
          mouseEvent.shiftKey ||
          mouseEvent.altKey
        ) {
          // Respect modifier-key/middle-click "open in new tab" behavior.
          return;
        }
        const itemId = link.dataset.itemId;
        const href = link.getAttribute('href');
        if (!itemId || !href) return;

        event.preventDefault();
        void openReleasePanel(href, itemId, true, kind);
      });
    });
  });

  panel.querySelector<HTMLButtonElement>('[data-panel-close]')?.addEventListener('click', () => {
    closeReleasePanel(true, releaseHref);
  });
  getBackdrop()?.addEventListener('click', () => {
    closeReleasePanel(true, releaseHref);
  });

  if (globalListenersAttached) return;
  globalListenersAttached = true;

  document.addEventListener('keydown', (event) => {
    const current = getPanel();
    if (event.key === 'Escape' && current && !current.hidden) {
      closeReleasePanel(true, releaseHref);
    }
  });

  window.addEventListener('popstate', (event) => {
    const state = event.state as ReleasePanelHistoryState | null;
    if (state?.releasePanelItemId) {
      const kind: PanelKind = state.releasePanelKind === 'proposal' ? 'proposal' : 'detail';
      const link = findPanelLink(state.releasePanelItemId, kind);
      if (link) {
        const href = link.getAttribute('href');
        if (href) {
          void openReleasePanel(href, state.releasePanelItemId, false, kind);
          return;
        }
      }
    }
    closeReleasePanel(false);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeReleasePanel);
} else {
  initializeReleasePanel();
}
